import { FC, useEffect, useMemo, useState } from 'react';
import PaginationBar from '../../components/PaginationBar';
import { fetchInventories, PaginatedInventoriesResponse } from '../../api/inventories';
import { runSyncJob } from '../../api/syncData';
import { fetchLocations, Location } from '../../api/locations';

const StockPanel: FC = () => {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [data, setData] = useState<PaginatedInventoriesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedLocationId]);

  useEffect(() => {
    let active = true;
    setLocationsLoading(true);
    fetchLocations({ limit: 100 })
      .then((resp) => { if (active) setLocations(resp.data); })
      .catch(() => { /* ignore for filter */ })
      .finally(() => { if (active) setLocationsLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchInventories({
      page,
      limit,
      search: debouncedSearch || undefined,
      locationId: selectedLocationId || undefined,
    })
      .then((response) => {
        if (!isMounted) return;
        setData(response);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load inventory.');
      })
      .finally(() => { if (isMounted) setLoading(false); });

    return () => { isMounted = false; };
  }, [page, limit, debouncedSearch, selectedLocationId, refreshCounter]);

  const totalPages = useMemo(() => data?.totalPages ?? 1, [data?.totalPages]);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await runSyncJob('inventories');
      setRefreshCounter((c) => c + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to sync inventories.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search variants (name, UPC, code)"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
          />
          <div className="flex flex-1 items-center gap-2">
            <select
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              className="w-full sm:w-56 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={locationsLoading}
              aria-label="Filter by location"
            >
              <option value="">All locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            {selectedLocationId && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
                onClick={() => {
                  setSearch('');
                  setDebouncedSearch('');
                  setSelectedLocationId('');
                }}
              >
                <span>✕</span> Clear Filter
              </button>
            )}
          </div>
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync from ERP'}
        </button>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Variant</th>
              <th className="px-5 py-3 font-semibold">Location</th>
              <th className="px-5 py-3 font-semibold">Stock</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={3}>Loading inventory…</td>
              </tr>
            ) : error ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={3}>{error}</td>
              </tr>
            ) : !data || data.data.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={3}>No inventory records.</td>
              </tr>
            ) : (
              data.data.map((row) => (
                <tr key={row.id} className="even:bg-slate-50/60">
                  <td className="px-5 py-3 text-slate-700">{row.productVariant?.name ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-700">{row.location?.name ?? '—'}</td>
                  <td className="px-5 py-3 text-slate-700">{row.stock}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={data?.page ?? 1}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={
          <span>
            Showing {(page - 1) * limit + Math.min(limit, data?.data.length ?? 0)} of {data?.total ?? 0} inventory rows
          </span>
        }
        renderExtra={
          <span className="text-xs text-slate-400">Page {page} of {totalPages} (20 / page)</span>
        }
      />
    </div >
  );
};

export default StockPanel;
